import React, { createContext, useContext, useCallback, useEffect, useMemo, useState, ReactNode } from 'react';
import { useUser } from './UserContext';
import useWorkspaceChange from '../hooks/useWorkspaceChange';
import { getBrandConfig, BrandConfigEntry } from '../services/brandConfigService';
import { BrandSettingKey, BrandSettingValue, readBrandSetting } from '../config/brandSettings';

interface BrandConfigContextType {
    entries: BrandConfigEntry[];
    loading: boolean;
    error: string | null;
    // Returns the raw `value` string of the entry matching this key for the current workspace's
    // enseigne, or undefined when the key isn't configured for it (or the config hasn't loaded yet).
    getConfigValue: (key: string) => string | undefined;
    // Vrai quand la configuration de l'enseigne courante est chargée : avant, ne rien masquer ni afficher sur sa base
    ready: boolean;
    // Valeur typée d'un réglage (valeur par défaut BeOne si la clé est absente), voir config/brandSettings.ts
    setting: <K extends BrandSettingKey>(key: K) => BrandSettingValue<K>;
}

const BrandConfigContext = createContext<BrandConfigContextType | undefined>(undefined);

export const BrandConfigProvider = ({ children }: { children: ReactNode }) => {
    const { selectedWorkspace } = useUser();
    const enseigne = selectedWorkspace?.volumeName;

    const [entries, setEntries] = useState<BrandConfigEntry[]>([]);
    const [loading, setLoading] = useState<boolean>(!!enseigne);
    const [error, setError] = useState<string | null>(null);
    // Enseigne dont la configuration a fini de charger (succès ou erreur)
    const [loadedFor, setLoadedFor] = useState<string | null>(null);

    const fetchConfig = useCallback(async (currentEnseigne: string) => {
        setLoading(true);
        setError(null);
        try {
            const data = await getBrandConfig(currentEnseigne);
            setEntries(data);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Erreur de chargement de la configuration enseigne');
            setEntries([]);
        } finally {
            setLoading(false);
            setLoadedFor(currentEnseigne);
        }
    }, []);

    // Clears the previous enseigne's config immediately on workspace switch, instead of leaving it
    // visible (and usable as defaults) until the refetch for the new one resolves.
    useWorkspaceChange(() => {
        setEntries([]);
        setLoading(true);
        setLoadedFor(null);
    });

    useEffect(() => {
        if (!enseigne) {
            setEntries([]);
            setLoading(false);
            return;
        }
        fetchConfig(enseigne);
    }, [enseigne, fetchConfig]);

    const getConfigValue = useCallback(
        (key: string): string | undefined => entries.find(entry => entry.key === key)?.value,
        [entries]
    );

    const setting = useCallback(
        <K extends BrandSettingKey>(key: K) => readBrandSetting(key, getConfigValue),
        [getConfigValue]
    );

    // Une configuration en erreur compte comme chargée : les réglages prennent alors leurs valeurs par défaut
    const ready = !!enseigne && !loading && loadedFor === enseigne;

    const value = useMemo(
        () => ({ entries, loading, error, getConfigValue, ready, setting }),
        [entries, loading, error, getConfigValue, ready, setting]
    );

    return <BrandConfigContext.Provider value={value}>{children}</BrandConfigContext.Provider>;
};

export const useBrandConfig = (): BrandConfigContextType => {
    const context = useContext(BrandConfigContext);
    if (context === undefined) {
        throw new Error('useBrandConfig must be used within a BrandConfigProvider');
    }
    return context;
};
